// FieldWorks rebuild design system — the one breakpoint, in JavaScript.
//
// There are exactly two layouts, phone and desktop. This hook is how a
// component or an app asks which one it is in. It mirrors the media query in
// base.css, DesktopShell.css and Sheet.css, so all four agree.
//
// Apps must not add breakpoints of their own — spec §6, "Two layouts. Not
// eight breakpoints." If a layout needs to change shape, it changes here.
//
// ── A PHONE IN LANDSCAPE IS STILL A PHONE ─────────────────────────────────
//
// The rule was width alone, and it was wrong. Owner, 2026-09-16, holding an
// iPhone 12: "iphone 12 has pages that still have to rotate for some buttons
// to appear … when it rotates it looks more like the desktop edition. WHY IS
// THE MOBILE APP SHOWING AS DESKTOP".
//
// Measured in a real browser before changing anything. The built app, driven
// as an iPhone 12, every pressable control harvested:
//
//   390x844 portrait    the phone shell — five tabs, no sidebar
//   844x390 landscape   the DESKTOP shell — WORK, Overview, Jobs, Schedule,
//                       DISPATCH, Crew board, Right now, Approvals, MONEY,
//                       Invoices, Estimates, Payroll, RECORDS, INSIGHT,
//                       MY DAY, ADMIN, YOU, Sign out, Account menu
//
// 280px of sidebar on a screen 390px TALL. An iPhone 12 is 390px wide in
// portrait and 844px wide in landscape, so rotating carried it across a
// 768px line — and no width alone can tell an 844px phone from an 844px
// browser window.
//
// The CLASSIC stylesheets already knew this: index.css and App.css carry a
// height-plus-pointer clause, and the suite that guards those copies says why
// in as many words. That knowledge never reached client/src/ui/fw/, which is
// the shell this app actually renders — the two trees are guarded separately
// and only ever had to agree with themselves. This one now carries the clause
// the other learned first.
//
// `pointer: coarse` is what keeps a short desktop WINDOW out of it — somebody
// with a 1400x420 window has a mouse and wants the sidebar. 500px because no
// phone is that tall in landscape and no tablet is that short: an iPhone 16
// Pro Max is 440, an iPad mini 744.
//
//   iPhone 12 landscape   844x390, coarse  -> phone    (the report)
//   iPhone 12 portrait    390x844, coarse  -> phone    (unchanged, by width)
//   iPad landscape       1080x810, coarse  -> desktop  (taller than 500)
//   short desktop window 1400x420, fine    -> desktop  (pointer)
//
// ── 500 here, 560 in the classic sheets. Why, and what it costs ────────────
//
// index.css and App.css use `(max-width: 768px), (max-height: 560px) and
// (pointer: coarse)`, and they are still imported globally (index.js, App.js)
// — index.css sets `input, select, textarea { font-size: 16px }` inside its
// copy, which reaches this app too. So the two do not style the same things,
// but they do both answer "is this a phone", and between 501 and 560px tall on
// a coarse pointer they answer differently.
//
// The numbers are not a drift; both are safe, and 500 is the tighter of two
// safe choices. The band has to sit above every phone in landscape and below
// every tablet in landscape: an iPhone 16 Pro Max is 440, an iPad mini 744. So
// anything from ~450 to ~740 sorts every real device correctly, and 500 and
// 560 are both inside it. Nothing this company owns is 501-560px tall.
//
// They are deliberately NOT unified here. Doing so means editing the classic
// global sheets, which style the signed-out screens and the two pages CLIENTS
// see, plus the five-copy guard over them (phoneShellQuery.test.js) — real
// blast radius for a band with no device in it. If it is ever unified, the
// rebuild's 500 is the number to keep: it is the one with the measurements
// written beside it.

import { useEffect, useState } from 'react';

export const FW_PHONE_MAX = 768;
/** No phone is taller than this in landscape; no tablet is this short. */
export const FW_PHONE_LANDSCAPE_MAX_HEIGHT = 500;
export const FW_PHONE_QUERY = `(max-width: ${FW_PHONE_MAX - 0.02}px), `
  + `(max-height: ${FW_PHONE_LANDSCAPE_MAX_HEIGHT}px) and (pointer: coarse)`;

/**
 * Is the viewport phone-sized right now? Prefers matchMedia; falls back to
 * innerWidth where matchMedia is missing (jsdom, very old WebViews).
 */
export function isPhoneViewport() {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    const mql = window.matchMedia(FW_PHONE_QUERY);
    if (mql && typeof mql.matches === 'boolean') return mql.matches;
  }
  // The fallback (jsdom, very old WebViews). It used to be innerWidth alone,
  // which answers "desktop" for the exact device this file is about, so it
  // carries the landscape case too. It cannot ask about `pointer`, so it uses
  // the shape instead: short AND wider than tall is a phone on its side.
  if (typeof window.innerWidth !== 'number') return false;
  if (window.innerWidth < FW_PHONE_MAX) return true;
  const h = window.innerHeight;
  return typeof h === 'number' && h > 0
    && h <= FW_PHONE_LANDSCAPE_MAX_HEIGHT && window.innerWidth > h;
}

/**
 * Is the viewport NARROW — a width question, not a device question.
 *
 * ── Why this exists, and why it is not usePhoneLayout ──────────────────────
 *
 * Some decisions are about the DEVICE ("does a sidebar make sense here?") and
 * some are about the ROOM ("does a second column fit?"). The landscape clause
 * above answers the first. Asking it for the second gets the wrong answer, and
 * it did: `OfficePage.DetailActions` hides its "Full screen" button on a
 * phone, on the recorded premise that the detail aside already has a row to
 * itself and full width, so the control would change nothing. That premise is
 * about ROOM. Measured on /jobs?open=1, coarse pointer, 2026-09-16:
 *
 *   390x844   aside 358 of a 358 row   full width
 *   844x390   aside 812 of an 812 row  full width
 *   600x900   aside 568 of a 568 row   full width
 *   956x440   aside 392 BESIDE a 512 main — it does not wrap at all
 *
 * ── THAT TABLE IS THE SECOND VERSION, AND THE FIRST ONE'S FATE IS THE POINT ─
 *
 * It read `844x390  aside 360px of an 812px row  2.3x narrower than the row`,
 * in THREE files, and the argument built on it was that hiding "Full screen"
 * on a landscape phone lost a button that widened the detail 2.3x. That was
 * true when it was measured and false eight commits later: the same PR added
 * `flex-grow: 1` to `.or-split__aside` inside the phone block, so the aside
 * now takes whatever line it lands on. Caught in review, not by me — I wrote
 * both halves and did not connect them.
 *
 * So the premise "the aside already has a row to itself at full width" now
 * HOLDS wherever the phone query matches, 600px and 844x390 included. It is
 * not a proxy any more; it is the thing itself.
 *
 * ── Which leaves the button needing a DIFFERENT reason, and it has one ─────
 *
 * `?view=full` does not only widen the aside. `Split` UNMOUNTS the list
 * (OfficePage.js), so full screen is the difference between a record you
 * scroll to and a record that is the page. On a screen 390px tall that is the
 * larger of the two effects, and it is why the button still earns its place at
 * 844x390 even though the width argument for it is gone.
 *
 * ── And it leaves an OPEN QUESTION, stated rather than assumed away ────────
 *
 * If the reason is "the list is in the way", that is just as true at 390x844,
 * where the button is still hidden. The old premise answered this — no room
 * gained, no button — and the old premise is dead. Nobody has measured whether
 * a crew member on a portrait phone wants it. Do not read this hook's split as
 * settling that; it settles which viewports have a wide aside, which is no
 * longer the same question.
 *
 * ── It is a proxy that happens to COINCIDE, which is not the same as being
 *    the measurement ───────────────────────────────────────────────────────
 *
 * This paragraph used to open "It is still a PROXY" and claim `.or-split__aside`
 * is `flex: 0 1 360px` "with no grow, so once it wraps it is 360px", and that
 * at 600px the aside is 360 of ~568. Both are false since `flex-grow: 1` went
 * into the phone block, and the file said so NINETEEN LINES ABOVE while this
 * still said the opposite. Caught in review, on the pull request whose entire
 * product was comment accuracy — twice in a row on the same block, which is
 * what fwPhoneQuery.test.js §9 now exists to stop.
 *
 * The honest version. A width query is not a container measurement; it only
 * gives the same answer here because `flex-grow: 1` is scoped to THE SAME
 * QUERY this hook asks (OfficePage.css's phone block). Two independent things
 * agreeing by construction, not one thing measured. Move either — widen the
 * grow to a container query, change this breakpoint — and they diverge
 * silently, and the symptom is a control that appears or vanishes at a width
 * nobody chose.
 *
 * So the coincidence is the thing to protect, and §9 protects it: if that
 * declaration leaves the phone block, or any file starts describing the aside
 * as not growing, the suite says so.
 *
 * `RecordList` makes the same distinction and resolves it properly, against
 * its own container. That is still the better tool and still the right pointer
 * for whoever revisits this. This is the viewport form, for a control in a
 * page header that has no container to measure.
 */
export const FW_NARROW_QUERY = `(max-width: ${FW_PHONE_MAX - 0.02}px)`;

export function isNarrowViewport() {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    const mql = window.matchMedia(FW_NARROW_QUERY);
    if (mql && typeof mql.matches === 'boolean') return mql.matches;
  }
  return typeof window.innerWidth === 'number' && window.innerWidth < FW_PHONE_MAX;
}

/** The subscription half, shared by both hooks so they cannot drift. */
function useMediaMatch(query, read) {
  const [on, setOn] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => setOn(read());
    update();

    if (typeof window.matchMedia === 'function') {
      const mql = window.matchMedia(query);
      if (mql) {
        // Safari < 14 has only the deprecated pair.
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', update);
          return () => mql.removeEventListener('change', update);
        }
        if (typeof mql.addListener === 'function') {
          mql.addListener(update);
          return () => mql.removeListener(update);
        }
      }
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
    // `read` is in the deps rather than suppressed. Both callers pass a
    // module-scope function, so the identity is stable and this never re-runs;
    // a future caller passing an inline one would re-subscribe each render —
    // wasteful, but correct, which a disable comment would not have been.
  }, [query, read]);

  return on;
}

export function useNarrowViewport() {
  return useMediaMatch(FW_NARROW_QUERY, isNarrowViewport);
}

export function usePhoneLayout() {
  return useMediaMatch(FW_PHONE_QUERY, isPhoneViewport);
}
